/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Modal,
} from 'antd';
import { formatReceivedValues } from 'utils/formatValues';
import { onError } from 'utils/handlers';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import Select from 'components/Select';
import {
  getEstadosGlobales,
  getTiposDeNomina,
} from 'api/catalogos/index';
import {
  getDeduccionesTiposDeNominas,
  postDeduccionTipoDeNomina,
  deleteDeduccionTipoDeNomina,
  patchDeduccionTipoDeNomina,
  getDeduccionTipoDeNomina,
} from 'api/nominas/deducciones/deduccionesTiposDeNominas';
import { checkToken } from 'api/utils';

export const deduccionTipoNominaPermission = {
  permissionModel: 'deducciontipodenomina',
  catalogsModels: [],
  tabsModels: [],
};

const DeduccionTipoDeNomina = ({
  permission,
  selected,
  estatusInactivo,
}) => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [estados, setEstados] = useState([]);
  const [tipoNomina, setTipoNomina] = useState([]);

  const formInstances = [
    form,
  ];

  const formatReceivedClone = (values) => {
    if (Array.isArray(values)) {
      let valueClone = values.map((e) => formatReceivedValues(e));
      valueClone = valueClone.map((e) => ({
        ...e,
        estado_global: e?.estados_globales,
      }));
      return valueClone;
    }
    return formatReceivedValues(values);
  };

  const fetchData = async () => {
    setLoading(true);
    const _data = await getDeduccionesTiposDeNominas({ deduccion: selected });
    setData(formatReceivedClone(_data));
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _estados,
        _tipoNomina,
        _data,
      ] = await Promise.all([
        getEstadosGlobales(),
        getTiposDeNomina(),
        getDeduccionesTiposDeNominas({ deduccion: selected }),
      ]);
      if (mounted) {
        setEstados(_estados);
        setTipoNomina(_tipoNomina);
        setData(formatReceivedClone(_data));
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const onCancel = () => {
    setSelectedRowKeys([]);
    setVisible(false);
    form.resetFields();
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const match = responseData || data.find((item) => item.id === key);
    const _deduccion = await getDeduccionTipoDeNomina(match.id);
    setTimeout(() => {
      if (_deduccion) {
        form.resetFields();
        setTimeout(() => {
          form.setFieldsValue(formatReceivedClone(_deduccion));
          setVisible(true);
        });
      }
    });
    setLoading(false);
  };

  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = {
        ...form.getFieldsValue(),
      };
      const [key] = selectedRowKeys;
      // eslint-disable-next-line max-len
      const saved = await (key ? patchDeduccionTipoDeNomina(key, { ...values, deduccion: selected }) : postDeduccionTipoDeNomina({ ...values, deduccion: selected }));
      if (saved) {
        if (_continue) {
          await fetchData();
          setSelectedRowKeys([saved.id]);
          onClickEdit(saved);
        } else {
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(
        err,
        setLoading,
        formInstances,
      );
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await deleteDeduccionTipoDeNomina(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const columns = [
    {
      titleText: 'Tipo de Nómina',
      dataIndex: 'tipo_de_nomina',
      key: 'tipo_de_nomina',
      width: 200,
      render: (val) => tipoNomina.find((e) => e.id === val)?.descripcion,
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estado_global',
      key: 'estado_global',
      width: 200,
      render: (val) => estados.find((e) => e.id === val)?.descripcion,
    },
  ];

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  const rules = {
    required: [requiredRule],
    contratacion: [requiredRule],
    estatus: [requiredRule],
  };

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          cols={columns}
          data={data}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd,
            onClickEdit: () => onClickEdit(),
            onClickDelete,
          }}
          disableAdd={estatusInactivo}
          disableDelete={estatusInactivo}
          permission={permission}
          allowExportPDF={false}
        />
        <Modal
          open={open}
          closable={false}
          maskClosable={!loading}
          keyboard={!loading}
          footer={null}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Tipo de Nómina`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
            />
            )}
          onCancel={onCancel}
          forceRender
          width="40%"
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            scrollToFirstError
            name="general"
          >
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="tipo_de_nomina"
                label="Tipo de Nómina"
                rules={rules.required}
                hasFeedback
              >
                <Select
                  dataSource={tipoNomina}
                  disabled={estatusInactivo}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="estados_globales"
                label="Estatus"
                rules={rules.estatus}
                hasFeedback
                initialValue={1}
              >
                <Select
                  labelProp="descripcion"
                  keyProp="id"
                  dataSource={estados}
                  disabled={!selectedRowKeys.length || estatusInactivo}
                />
              </Form.Item>
            </Col>
            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Form>
        </Modal>
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content={`Tipo de Nómina ${tipoNomina.find((e) => e.id === form.getFieldValue('tipo_de_nomina'))?.descripcion}`}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default DeduccionTipoDeNomina;
